/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Component,
  ComponentRef,
  ViewChild,
  ViewEncapsulation,
  NgZone,
  OnDestroy,
  ElementRef,
  EventEmitter,
  Inject,
  Optional,
} from '@angular/core';
import {
  animate,
  trigger,
  state,
  style,
  transition,
  AnimationEvent,
} from '@angular/animations';
import {DOCUMENT} from '@angular/platform-browser';
import {BasePortalHost, ComponentPortal, PortalHostDirective, TemplatePortal} from '../core';
import {MdDialogConfig} from './dialog-config';
import {FocusTrapFactory, FocusTrap} from '../core/a11y/focus-trap';
/**
 * Throws an exception for the case when a ComponentPortal is
 * attached to a DomPortalHost without an origin.
 * \@docs-private
 * @return {?}
 */
export function throwMdDialogContentAlreadyAttachedError() {
  throw Error('Attempting to attach dialog content after content is already attached');
}
/**
 * Internal component that wraps user-provided dialog content.
 * Animation is based on https://material.io/guidelines/motion/choreography.html.
 * \@docs-private
 */
export class MdDialogContainer extends BasePortalHost {
/**
 * The portal host inside of this container into which the dialog content will be loaded.
 */
_portalHost: PortalHostDirective;
/**
 * The class that traps and manages focus within the dialog.
 */
private _focusTrap: FocusTrap;
/**
 * Element that was focused before the dialog was opened. Save this to restore upon close.
 */
private _elementFocusedBeforeDialogWasOpened: HTMLElement = null;
/**
 * Reference to the global document object.
 */
private _document: Document;
/**
 * The dialog configuration.
 */
_config: MdDialogConfig;
/**
 * State of the dialog animation.
 */
_state: 'void' | 'enter' | 'exit' = 'enter';
/**
 * Emits the current animation state whenever it changes.
 */
_onAnimationStateChange = new EventEmitter<AnimationEvent>();
/**
 * @param {?} _ngZone
 * @param {?} _elementRef
 * @param {?} _focusTrapFactory
 * @param {?} _document
 */
constructor(
private _ngZone: NgZone,
private _elementRef: ElementRef,
private _focusTrapFactory: FocusTrapFactory,
      _document: any) {

    super();
    this._document = _document;
  }
/**
 * Attach a ComponentPortal as content to this dialog container.
 * @template T
 * @param {?} portal Portal to be attached as the dialog content.
 * @return {?}
 */
attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
    if (this._portalHost.hasAttached()) {
      throwMdDialogContentAlreadyAttachedError();
    }

    this._savePreviouslyFocusedElement();
    return this._portalHost.attachComponentPortal(portal);
  }
/**
 * Attach a TemplatePortal as content to this dialog container.
 * @param {?} portal Portal to be attached as the dialog content.
 * @return {?}
 */
attachTemplatePortal(portal: TemplatePortal): Map<string, any> {
    if (this._portalHost.hasAttached()) {
      throwMdDialogContentAlreadyAttachedError();
    }

    this._savePreviouslyFocusedElement();
    return this._portalHost.attachTemplatePortal(portal);
  }
/**
 * Moves the focus inside the focus trap.
 * @return {?}
 */
private _trapFocus() {
    if (!this._focusTrap) {
      this._focusTrap = this._focusTrapFactory.create(this._elementRef.nativeElement);
    }

    // If were to attempt to focus immediately, then the content of the dialog would not yet be
    // ready in instances where change detection has to run first. To deal with this, we simply
    // wait for the microtask queue to be empty.
    this._focusTrap.focusInitialElementWhenReady();
  }
/**
 * Restores focus to the element that was focused before the dialog opened.
 * @return {?}
 */
private _restoreFocus() {
    const /** @type {?} */ toFocus = this._elementFocusedBeforeDialogWasOpened;

    // We need the extra check, because IE can set the `activeElement` to null in some cases.
    if (toFocus && 'focus' in toFocus) {
      toFocus.focus();
    }

    if (this._focusTrap) {
      this._focusTrap.destroy();
    }
  }
/**
 * Saves a reference to the element that was focused before the dialog was opened.
 * @return {?}
 */
private _savePreviouslyFocusedElement() {
    if (this._document) {
      this._elementFocusedBeforeDialogWasOpened = /** @type {?} */(( this._document.activeElement as HTMLElement));
    }
  }
/**
 * Callback, invoked whenever an animation on the host completes.
 * @param {?} event
 * @return {?}
 */
_onAnimationDone(event: AnimationEvent) {
    this._onAnimationStateChange.emit(event);

    if (event.toState === 'enter') {
      this._trapFocus();
    } else if (event.toState === 'exit') {
      this._restoreFocus();
      this._onAnimationStateChange.complete();
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-dialog-container, mat-dialog-container',
  templateUrl: 'dialog-container.html',
  styleUrls: ['dialog.css'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideDialog', [
      // Note: The `enter` animation doesn't transition to something like `translate3d(0, 0, 0)
      // scale(1)`, because for some reason specifying the transform explicitly, causes IE both
      // to blur the dialog content and decimate the animation performance. Leaving it as `none`
      // solves both issues.
      state('enter', style({ transform: 'none', opacity: 1 })),
      state('void', style({ transform: 'translate3d(0, 25%, 0) scale(0.9)', opacity: 0 })),
      state('exit', style({ transform: 'translate3d(0, 25%, 0)', opacity: 0 })),
      transition('* => *', animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')),
    ])
  ],
  host: {
    'class': 'mat-dialog-container',
    '[attr.role]': '_config?.role',
    '[@slideDialog]': '_state',
    '(@slideDialog.done)': '_onAnimationDone($event)',
  },
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: NgZone, },
{type: ElementRef, },
{type: FocusTrapFactory, },
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [DOCUMENT, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_portalHost': [{ type: ViewChild, args: [PortalHostDirective, ] },],
};
}

function MdDialogContainer_tsickle_Closure_declarations() {
/** @type {?} */
MdDialogContainer.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdDialogContainer.ctorParameters;
/** @type {?} */
MdDialogContainer.propDecorators;
/**
 * The portal host inside of this container into which the dialog content will be loaded.
 * @type {?}
 */
MdDialogContainer.prototype._portalHost;
/**
 * The class that traps and manages focus within the dialog.
 * @type {?}
 */
MdDialogContainer.prototype._focusTrap;
/**
 * Element that was focused before the dialog was opened. Save this to restore upon close.
 * @type {?}
 */
MdDialogContainer.prototype._elementFocusedBeforeDialogWasOpened;
/**
 * Reference to the global document object.
 * @type {?}
 */
MdDialogContainer.prototype._document;
/**
 * The dialog configuration.
 * @type {?}
 */
MdDialogContainer.prototype._config;
/**
 * State of the dialog animation.
 * @type {?}
 */
MdDialogContainer.prototype._state;
/**
 * Emits the current animation state whenever it changes.
 * @type {?}
 */
MdDialogContainer.prototype._onAnimationStateChange;
/** @type {?} */
MdDialogContainer.prototype._ngZone;
/** @type {?} */
MdDialogContainer.prototype._elementRef;
/** @type {?} */
MdDialogContainer.prototype._focusTrapFactory;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
